import { internal } from '@croissants/contracts';
import { useState } from 'react';
import { useEndpointQuery } from './api';

/**
 * The service day a dated screen opens on (PRD OPS-001, TST-003).
 *
 * The platform's "today", never the browser's. Outside production the platform
 * runs on the rehearsal clock, and `new Date()` in the operator's browser is
 * nobody's clock here: the admin dashboard used to open on the browser's date and
 * render an empty board while 34 orders were in flight one day over - printing
 * the server's own time on the same line. `GET /internal/time` answers with the
 * platform clock and its Paris service day, and every dated screen defaults
 * from that.
 *
 * Order of precedence:
 *  1. what the operator typed into the date input (their explicit choice);
 *  2. `?serviceDay=YYYY-MM-DD` in the URL (deep links between screens);
 *  3. the platform's own today, once `/internal/time` has answered.
 *
 * `day` is `''` until one of those exists, and a screen must not query with an
 * empty day - pass `enabled: day !== ''` (or omit the parameter) so the first
 * request is either correctly dated or deferred, never browser-dated.
 */
export function usePlatformDay(): {
  /** The effective service day, or '' while the platform day is still loading. */
  readonly day: string;
  /**
   * Precedence rules 1 and 2 only: the operator's typed choice, else the URL's
   * `?serviceDay=`, else ''. Unlike `day` this never waits on `/internal/time`, so a
   * screen whose endpoint already defaults to the platform's own today
   * server-side (the admin dashboard) can fire its first request immediately -
   * '' means "omit the parameter", not "browser-dated". Screens whose endpoint
   * gives an omitted day a DIFFERENT meaning (the Orders working set) must keep
   * gating on `day`.
   */
  readonly explicitDay: string;
  /** The operator's explicit choice; wire it to the date input's onChange. */
  readonly setDay: (next: string) => void;
  /** The platform's own today ('' while loading) - for secondary defaults. */
  readonly platformToday: string;
} {
  const [chosen, setChosen] = useState('');
  const time = useEndpointQuery(internal.getPlatformTime, {}, { staleTime: 30_000 });
  const platformToday = time.data?.serviceDay ?? '';
  const explicitDay = chosen !== '' ? chosen : (urlServiceDay() ?? '');
  const day = explicitDay !== '' ? explicitDay : platformToday;
  return { day, explicitDay, setDay: setChosen, platformToday };
}

/**
 * The platform clock as an INSTANT, or null until `/internal/time` has answered
 * (PRD OPS-001, TST-003).
 *
 * The same read `usePlatformDay` makes - one query key, so a screen asking for
 * both makes one request - answering the other half of it: a screen doing
 * wall-clock arithmetic needs the instant, not the service day.
 *
 * Null rather than a browser-dated default, deliberately, so each caller states
 * its own fallback. A distance ("2 hours ago") can fall back to the browser's
 * clock, which is the same clock in production and an hour out at worst on the
 * rehearsal one. A stated CONSEQUENCE cannot: the force panel promises an
 * instant the server will compute for itself, and a promise made on the wrong
 * clock is a promise the fleet does not keep.
 */
export function usePlatformNow(): Date | null {
  /*
   * It REFETCHES, unlike `usePlatformDay` beside it, and the two share one
   * query key so this is what the pair does. A service day does not move while
   * somebody watches a screen; an instant does, and every reader of this hook
   * renders a distance from it - a fleet list left open on a wall would
   * otherwise say "2 minutes ago" for the rest of the morning, because nothing
   * else on it changes either.
   */
  const time = useEndpointQuery(
    internal.getPlatformTime,
    {},
    {
      staleTime: 30_000,
      refetchInterval: 30_000,
    },
  );
  const now = time.data?.now;
  return now === undefined ? null : new Date(now);
}

const SERVICE_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** `?serviceDay=` from the URL, when present and well-formed. */
export function urlServiceDay(): string | null {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('serviceDay');
  return raw !== null && SERVICE_DAY_RE.test(raw) ? raw : null;
}
